import type { ReactNode } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { cn } from "@/cn";
import type { DataTableSortDirection } from "./useClientTableSort";

export interface Column<T> {
  key: keyof T | string;
  header: string;
  render?: (item: T) => ReactNode;
  className?: string;
  sortable?: boolean;
}

export type DataTableDensity = "comfortable" | "compact";

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  emptyMessage?: string;
  onRowClick?: (item: T) => void;
  className?: string;
  density?: DataTableDensity;
  sortKey?: string | null;
  sortDirection?: DataTableSortDirection;
  onSortChange?: (key: string) => void;
  /** Resolve a stable row key when item has no `id`. */
  getRowId?: (item: T, index: number) => string;
}

const DENSITY_CELL: Record<DataTableDensity, string> = {
  comfortable: "px-4 py-2.5 text-sm",
  compact: "px-3 py-2 text-xs",
};

export function DataTable<T extends { id?: string }>({
  data,
  columns,
  loading = false,
  emptyMessage = "No data available",
  onRowClick,
  className = "",
  density = "comfortable",
  sortKey,
  sortDirection = "asc",
  onSortChange,
  getRowId,
}: DataTableProps<T>) {
  const cellPad = DENSITY_CELL[density];

  if (loading) {
    return (
      <div className={cn("animate-pulse space-y-3", className)}>
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-10 rounded bg-muted/30" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div
        className={cn(
          "rounded-lg border border-border bg-card p-8 text-center",
          className,
        )}
      >
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "overflow-x-auto rounded-lg border border-border",
        className,
      )}
    >
      <table className="w-full">
        <thead>
          <tr className="border-b border-border bg-muted/20">
            {columns.map((column) => {
              const key = String(column.key);
              const sortable = Boolean(column.sortable && onSortChange);
              const active = sortable && sortKey === key;
              const Icon = !active
                ? ArrowUpDown
                : sortDirection === "asc"
                  ? ArrowUp
                  : ArrowDown;
              return (
                <th
                  key={key}
                  className={cn(
                    "text-left font-medium text-muted-foreground",
                    cellPad,
                    column.className,
                  )}
                  aria-sort={
                    active
                      ? sortDirection === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => onSortChange?.(key)}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-md px-0.5 py-0.5 transition hover:text-foreground",
                        active ? "text-foreground" : "text-muted-foreground",
                      )}
                    >
                      <span>{column.header}</span>
                      <Icon
                        className={cn(
                          "h-3.5 w-3.5 shrink-0",
                          active ? "text-brand" : "text-faint",
                        )}
                        aria-hidden
                      />
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr
              key={getRowId?.(item, index) ?? item.id ?? index}
              onClick={() => onRowClick?.(item)}
              className={cn(
                "border-b border-border last:border-0 transition",
                onRowClick && "cursor-pointer hover:bg-muted/10",
              )}
            >
              {columns.map((column) => (
                <td
                  key={String(column.key)}
                  className={cn(
                    "text-foreground",
                    cellPad,
                    column.className,
                  )}
                >
                  {column.render
                    ? column.render(item)
                    : String(item[column.key as keyof T] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
